import { FieldView } from '../../FieldEditor';
import { renderSampleTimeRange } from './helper';
import {
  InternalApiScenario,
  InternalApiScenarioContentTestResultInformation,
  InternalApiScenarioTestResultStatus,
  InternalApiScenarioTypeV2,
} from '../../../api-internal';
import React from 'react';
import dayjs from 'dayjs';
import { FULL_DATE_TIME_FORMAT, pluralize } from '../../../common/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import FormattedDuration from '../../../common/datetime/FormattedDuration';
import { millisToTimeUnits } from '../../../common/versioning/helper';
import accounting from 'accounting';

const renderExecutionTime = (testResult: InternalApiScenarioContentTestResultInformation) => {
  return (
    <div>
      <div className='m-b-4'>
        <span> {dayjs(testResult.startedTime).format(FULL_DATE_TIME_FORMAT)}</span>
        <FontAwesomeIcon icon={faArrowRight} className='m-x-8' />
        <span>
          {dayjs(testResult.startedTime)
            .add(testResult.executionTotalDuration || 0, 'ms')
            .format(FULL_DATE_TIME_FORMAT)}
        </span>
      </div>
      <div className='m-b-4'>
        <div className='text-secondary'>
          <span>Elapsed time: </span>
          <FormattedDuration durationInMs={testResult.executionTotalDuration} withSuffix />
        </div>
      </div>
    </div>
  );
};

const renderEstimatedTime = (testResult: InternalApiScenarioContentTestResultInformation) => {
  const completedPercentage =
    dayjs(testResult.lastEntityTime).diff(dayjs(testResult.testDataTimeFrom)) /
    dayjs(testResult.testDataTimeTo).diff(dayjs(testResult.testDataTimeFrom));
  const timeToCompletion = millisToTimeUnits(
    (dayjs().diff(dayjs(testResult.startedTime)) * (1 - completedPercentage)) / completedPercentage
  );

  return (
    testResult.lastEntityTime && (
      <div>
        {timeToCompletion.hours != 0
          ? pluralize(timeToCompletion.hours + 1, 'hour', 'hours', true)
          : pluralize(timeToCompletion.minutes + 1, 'minute', 'minutes', true)}
      </div>
    )
  );
};

const renderScenarioRunSpeed = (testResult: InternalApiScenarioContentTestResultInformation, isPeriodic: boolean) => {
  const totalRunsCount = testResult?.executionBuckets.reduce((acc, bucket) => acc + bucket.count, 0) || 0;
  const orderedBuckets = testResult?.executionBuckets.sort((a, b) => (b.from || 0) - (a.from || 0));
  const bucketsUnit = isPeriodic ? 's' : 'ms';
  const bucketsDivider = bucketsUnit === 's' ? 1000 : 1;

  return (
    testResult.executionBuckets.length > 0 && (
      <div>
        <div>{totalRunsCount} runs</div>
        {orderedBuckets
          ?.filter((b) => b.count > 0)
          .map((bucket, index) => (
            <div key={'bucket' + index} className='m-b-4'>
              {bucket.from != null && bucket.to == null ? '>' + bucket.from / bucketsDivider : ''}
              {bucket.from == null && bucket.to != null ? '<' + bucket.to / bucketsDivider : ''}
              {bucket.from != null && bucket.to != null
                ? bucket.from / bucketsDivider + ' - ' + bucket.to / bucketsDivider
                : ''}
              {bucketsUnit}: {bucket.count} runs (
              {accounting.formatNumber((bucket.count / totalRunsCount) * 100, 0, '')}%)
            </div>
          ))}
      </div>
    )
  );
};

export const TestRunDetailsCard = ({
  testResult,
  scenario,
}: {
  testResult: InternalApiScenarioContentTestResultInformation;
  scenario: InternalApiScenario;
}) => {
  const isPeriodic = scenario?.type === InternalApiScenarioTypeV2.PERIODIC;
  const testTimeRangeTitle = isPeriodic ? 'Testing time range' : 'Sample data';

  return (
    <div className='scenario-card'>
      <div className='header'>Test run details</div>

      <div className='content'>
        <FieldView label={testTimeRangeTitle} value={<div>{renderSampleTimeRange(testResult)}</div>} />

        {testResult.status === InternalApiScenarioTestResultStatus.FINISHED && (
          <FieldView label='Execution Time' value={renderExecutionTime(testResult)} />
        )}

        {testResult.status === InternalApiScenarioTestResultStatus.FINISHED && (
          <FieldView label={`Scenario run speed`} value={renderScenarioRunSpeed(testResult, isPeriodic)} />
        )}

        {testResult.status === InternalApiScenarioTestResultStatus.RUNNING && (
          <FieldView label='Estimated time remaining' value={renderEstimatedTime(testResult)} />
        )}

        <FieldView
          label='Run on alerted entities only'
          value={<span className='text-capitalize'>{testResult.onAlertedEntitiesOnly.toString()}</span>}
        />
      </div>
    </div>
  );
};
